import React, { useState, useEffect, useLayoutEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './config/msalConfig';
import 'bootstrap/dist/css/bootstrap.min.css';
import Sidebar from './components/Sidebar';
import Login from './components/Login';
import Register from './components/Register';
import Profile from './components/Profile';
import ProtectedRoute from './components/ProtectedRoute';
import InitialRedirect from './components/InitialRedirect';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import GoogleCallback from './components/GoogleCallback';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import './App.css';
import './components/Global.css';
import API_BASE_URL from './config';

export { getFeaturePath } from './utils/routeUtils';

// Lazy load heavy components
const FileUpload = React.lazy(() => import('./components/FileUpload'));
const DocumentViewer = React.lazy(() => import('./components/DocumentViewer'));
const Home = React.lazy(() => import('./components/Home'));
const ChatAssist = React.lazy(() => import('./components/ChatAssist'));
const Designer = React.lazy(() => import('./components/Designer'));
const AutoTestData = React.lazy(() => import('./components/AutoTestData'));
const RequestHistoryPage = React.lazy(() => import('./components/RequestHistoryPage'));
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const ProjectPage = React.lazy(() => import('./components/ProjectPage'));
const CodeLensQAPage = React.lazy(() => import('./components/CodeLensQAPage'));
const CodeLensQAHistoryPage = React.lazy(() => import('./components/CodeLensQAHistoryPage'));

// Loading component
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
    <Spinner animation="border" role="status">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  </div>
);

const AppShell = ({ isAuthenticated, setIsAuthenticated }) => {
  const location = useLocation();
  const [userProfile, setUserProfile] = useState(null);
  const isAuthRoute = ['/login', '/register', '/forgot-password', '/reset-password', '/google-callback'].some((p) => location.pathname.startsWith(p));

  // Scroll to top on route change - use useLayoutEffect for synchronous execution
  useLayoutEffect(() => {
    // Immediate scroll to top (no smooth behavior for instant effect)
    window.scrollTo(0, 0);
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }, [location.pathname]);
  
  // Also use useEffect as a backup to catch any late-rendering content
  useEffect(() => {
    // Scroll after a small delay to catch any late-rendering content
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
      
      // Also scroll any scrollable containers to top
      const scrollableContainers = document.querySelectorAll('[data-scroll-container], .main-content, .scrollable');
      scrollableContainers.forEach(container => {
        if (container && container.scrollTop !== undefined) {
          container.scrollTop = 0;
        }
      });
    }, 0);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Fetch user profile when authenticated
  useEffect(() => {
    if (isAuthenticated && !isAuthRoute) {
      const fetchUserProfile = async () => {
        try {
          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
          if (token) {
            console.log('App.js: Fetching user profile automatically after login...');
            const response = await fetch(`${API_BASE_URL}/auth/profile`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
              const profileData = await response.json();
              console.log('App.js: Profile data loaded automatically:', profileData);
              console.log('App.js: Profile photo included:', profileData.profilePhoto ? 'Yes' : 'No');
              setUserProfile(profileData);
            } else {
              console.log('App.js: Profile fetch failed:', response.status);
            }
          }
        } catch (error) {
          console.error('App.js: Error fetching user profile:', error);
        }
      };

      // Fetch profile immediately after authentication
      fetchUserProfile();
    }
  }, [isAuthenticated, isAuthRoute]);

  return (
    <div className={`app-container ${isAuthenticated && !isAuthRoute ? 'has-sidebar' : ''} ${isAuthRoute ? 'auth' : ''}`} style={isAuthRoute ? { background: '#ffffff' } : {}}>
      {isAuthenticated && !isAuthRoute && <Sidebar setIsAuthenticated={setIsAuthenticated} userProfile={userProfile} />}
      <div className="main-content">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<InitialRedirect />} />
            <Route
              path="/home"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <Home userProfile={userProfile} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project/:id"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <ProjectPage userProfile={userProfile} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/brd-to-frd/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <FileUpload task="brd-to-frd" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/brd-to-frd"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <FileUpload task="brd-to-frd" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/generate-test-cases/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <FileUpload task="generate-test-cases" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/generate-test-cases"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <FileUpload task="generate-test-cases" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automation-scripts/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <FileUpload task="automation-scripts" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automation-scripts"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <FileUpload task="automation-scripts" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/view"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <DocumentViewer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/generate-bug-report/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <FileUpload task="generate-bug-report" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/generate-bug-report"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <FileUpload task="generate-bug-report" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/code-converter"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <FileUpload task="code-converter" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rest-assured-api-testing/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <FileUpload task="rest-assured-api-testing" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rest-assured-api-testing"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <FileUpload task="rest-assured-api-testing" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <Profile setUserProfile={setUserProfile} userProfile={userProfile} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/request-history"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <RequestHistoryPage userProfile={userProfile} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat-assist"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <ChatAssist />
                </ProtectedRoute>
              }
            />
            <Route
              path="/designer"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <Designer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/auto-test-data/projectId/:projectId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <AutoTestData />
                </ProtectedRoute>
              }
            />
            <Route
              path="/auto-test-data"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <AutoTestData />
                </ProtectedRoute>
              }
            />
            <Route
              path="/codelens-qa"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <CodeLensQAPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/codelens-qa/workflow/:workflowId"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <CodeLensQAPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/codelens-qa/history"
              element={
                <ProtectedRoute setIsAuthenticated={setIsAuthenticated}>
                  <CodeLensQAHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<Login setIsAuthenticated={setIsAuthenticated} />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/google-callback" element={<GoogleCallback />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
          </Routes>
        </Suspense>
      </div>
      {isAuthenticated && !isAuthRoute && <ChatAssist />}
      {!isAuthRoute && <Footer />}
    </div>
  );
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Create MSAL instance
  const msalInstance = new PublicClientApplication(msalConfig);

  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || localStorage.getItem('microsoftToken');
    setIsAuthenticated(!!token);
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
      <Router>
        <AppShell isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} />
      </Router>
    </MsalProvider>
  );
};

export default App;
